import { User as Auth0User } from '@auth0/auth0-react';
import {
  User,
  Workspace,
  WorkspacePermission,
  WorkspaceStatusType,
} from '@app-types/blackroom-api';
import { RowUser } from '@app/types/blackroom-app';

/**
 * Parses user first/name or passed full-name
 * and yields initials and full-name (if not passed)
 * @param {User} user User instance
 * @param {string} overridingFullName When we know fullname in advance
 * or want to force it
 * @returns
 */
export const parseUserName = (
  user: Partial<Pick<User, 'firstName' | 'lastName' | 'auth0Id' | 'email'>>,
  overridingFullName?: string,
): { fullName: string; initials: string } => {
  if (!user) {
    return { fullName: '', initials: '' };
  }
  const { firstName, lastName } = user;
  let fullName = overridingFullName;

  if (user.auth0Id || firstName) {
    fullName =
      overridingFullName ?? `${firstName}${lastName ? ` ${lastName}` : ''}`;
  } else {
    fullName = user.email ?? '-';
  }

  let initials = 'BR';
  if (firstName && lastName) {
    initials = `${firstName[0]}${lastName[0]}`;
  } else if (firstName || lastName) {
    initials = [firstName ?? '', lastName ?? '']
      .filter((c) => c)[0]
      .substring(0, 1);
  } else if (fullName) {
    initials = fullName
      .split(' ')
      .map((c) => c[0])
      .join();
  }
  return { fullName, initials };
};

/**
 * Maps auth0 user object to Blackroom's
 *
 */
export const mapAuth0ToAPIUser = (
  auth0User?: Auth0User,
): Partial<User> | undefined => {
  if (!auth0User) {
    return undefined;
  }
  const { name, picture, email: auth0Email = '', sub: auth0Id } = auth0User;
  const email = auth0Email === null ? undefined : auth0Email;
  const [firstName, lastName] = name ? name.split(' ') : ['B', 'R'];
  return {
    firstName,
    lastName,
    auth0Id,
    email,
    pictureURL: picture,
  };
};

/**
 * Tells whether user is a workspace admin
 * @param user User to verify
 * @param workspace Workspace to check against
 * @returns {boolean} Whether user is workspace admin or not
 */
export const isWorkspaceAdmin = (
  user: User,
  workspace: Pick<Workspace, 'id'>,
) =>
  !!user?.workspaceUsers.find(
    (wu) => wu.workspace.id === workspace?.id && wu.role.isAdmin,
  );

/**
 * Tells whether user is part of workspace (whether suspended or not)
 * @param user User to verify
 * @param workspace Workspace to check against
 * @returns {boolean} Whether user is a workspace member
 */
export const isWorkspaceUser = (
  user: User,
  workspace: Pick<Workspace, 'id'>,
) => {
  const workspaceUser = user.workspaceUsers.find(
    (wu) => wu.workspace.id === workspace.id,
  );
  return !!workspaceUser;
};

export const isWorkspaceSeller = (
  user: User,
  workspace: Pick<Workspace, 'id'>,
) =>
  !!user?.workspaceUsers.find(
    (wu) => wu.workspace.id === workspace?.id && wu.role.name === 'Seller',
  );

export const hasWorkspacePermission = (
  user: User,
  workspace: Pick<Workspace, 'id'>,
  permission: keyof Omit<WorkspacePermission, '__typename'>,
) => {
  const workspaceUser = user.workspaceUsers.find(
    (wu) => wu.workspace.id === workspace.id,
  );
  return workspaceUser?.role.permissions[permission] === true;
};

/**
 * Tells whether user is suspended from workspace
 * @param user User to verify
 * @param workspace Workspace to check against
 * @returns {boolean} Whether user is suspended for the workspace or not
 */
export const isWorkspaceUserSuspended = (
  user: Partial<Pick<RowUser, 'workspaceUsers'>>,
  workspace: Pick<Workspace, 'id'>,
) => {
  const workspaceUser = user.workspaceUsers?.find(
    (wu) => wu.workspace.id === workspace.id,
  );
  if (workspaceUser && workspaceUser.isSuspended) {
    return true;
  }
  return false;
};

/**
 * Tells whether user can access workspace
 * @param user User to verify
 * @param workspace Workspace to check against(offline or live mode)
 * @returns {boolean} Whether user can access workspace
 */
export const canAccessWorkspace = (
  user: User,
  workspace: Pick<Workspace, 'id' | 'status'>,
) => {
  if (isWorkspaceAdmin(user, workspace)) {
    return true;
  }
  if (isWorkspaceSeller(user, workspace)) {
    return true;
  }
  if (
    !!workspace &&
    workspace.status === WorkspaceStatusType.Live &&
    !isWorkspaceUserSuspended(user, workspace)
  ) {
    return true;
  }
  return false;
};
